import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import type { StageReadinessRow } from '@/hooks/useDviStageReadiness';
import { stageBadgeClass, stageLabel } from './stageColors';

interface Props {
  rows: StageReadinessRow[];
  onSelect: (row: StageReadinessRow) => void;
  // B-19 (P-05): the readiness RPC refuses (unavailable) until shop-scoped confidence exists. Say so rather than
  // showing every stage as "none".
  unavailable?: boolean;
}

const ORDER = ['automation', 'job_suggestion', 'prefill', 'suggestion', 'none'];

export function StageReadinessPanel({ rows, onSelect, unavailable = false }: Props) {
  const grouped = ORDER.map((stage) => ({
    stage,
    items: rows.filter((r) => r.component_stage === stage),
  }));

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">Stage Readiness</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {unavailable && (
          <div className="text-xs text-muted-foreground italic">Component readiness is unavailable.</div>
        )}
        {!unavailable && grouped.map(({ stage, items }) => (
          <div key={stage}>
            <div className="flex items-center gap-2 mb-1">
              <Badge variant="outline" className={stageBadgeClass(stage)}>{stageLabel(stage)}</Badge>
              <span className="text-xs text-muted-foreground">{items.length}</span>
            </div>
            {items.length === 0 ? (
              <div className="text-xs text-muted-foreground italic pl-1">none</div>
            ) : (
              <div className="flex flex-wrap gap-1">
                {items.slice(0, 8).map((r) => (
                  <button
                    key={`${r.component}-${r.condition_type}`}
                    onClick={() => onSelect(r)}
                    className="text-[11px] px-2 py-0.5 rounded border border-border bg-background hover:bg-muted transition-colors"
                  >
                    {r.component} / {r.condition_type}
                  </button>
                ))}
                {items.length > 8 && <span className="text-[11px] text-muted-foreground self-center">+{items.length - 8}</span>}
              </div>
            )}
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
